import React from "react";

import { gridSpacing, colorTheme } from "@utils";

import SkeletonLoader from "../SkeletonLoader/SkeletonLoader";
import type { MarginInterface } from "../utilities/interfaces";
import { getMargins } from "../utilities/margin";

const CIRCLE_SIZES = {
  1: 20,
  2: 24,
  3: 28,
} as const;

const FONT_SIZES = {
  1: 12,
  2: 14,
  3: 16,
} as const;

interface BadgeProps extends MarginInterface {
  size?: 1 | 2 | 3;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  maxDisplayValue?: number;
  loading?: boolean;
}

const Badge = ({
  size = 1,
  style = {},
  children,
  maxDisplayValue,
  loading = false,
  m,
  mx,
  my,
  mt,
  mb,
  mr,
  ml,
}: BadgeProps) => {
  const getDisplayValue = () => {
    const parsedInt = parseInt(children + "");

    if (!isNaN(parsedInt) && maxDisplayValue && parsedInt > maxDisplayValue) {
      return `${maxDisplayValue}+`;
    }
    return children;
  };

  if (loading) {
    const circleSize = CIRCLE_SIZES[size];
    return (
      <SkeletonLoader
        width={circleSize}
        height={circleSize}
        style={{
          borderRadius: circleSize / 2,
          flexShrink: 0,
          ...getMargins({ m, mx, my, mt, mb, mr, ml }),
          ...style,
        }}
      />
    );
  }

  return (
    <div
      style={{
        minWidth: CIRCLE_SIZES[size],
        height: CIRCLE_SIZES[size],
        backgroundColor: colorTheme("neutralL4"),
        borderRadius: CIRCLE_SIZES[size] / 2,
        color: colorTheme("neutralL1"),
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        fontSize: FONT_SIZES[size],
        flexShrink: 0,
        paddingLeft: gridSpacing[1],
        paddingRight: gridSpacing[1],
        ...getMargins({ m, mx, my, mt, mb, mr, ml }),
        ...style,
      }}
    >
      {getDisplayValue()}
    </div>
  );
};

export default Badge;
